import energyPlans from './energyPlans'
import floorPlans from './floorPlans'
import inspectionForms from './inspectionForms'
import Constants from 'src/utilities/constants'
import axios from 'axios'
import Vue from 'vue'
import {ACCEPTED, CREATED, NO_CONTENT, NOT_FOUND, OK} from 'http-status-codes'
import {ptracDeleteEntity, ptracGetEntities, ptracPost, ptracPostEntity} from 'src/utilities/ptracApi'
import {
  createFolder,
  deleteFile,
  downloadFile,
  downloadFilesForSelectedPaths,
  downloadFlattenedPdf,
  fetchDirectoryContents,
  presignedS3PutUrlsFileUploads,
} from 'src/utilities/fileManagerApi'
import {addHeaders} from 'src/utilities/authHeader'
import {
  calculateUploadProgressInteger,
  fileSystemPathForTask,
  getRepeatCount,
  saveFileToBrowserDownloads
} from 'src/utilities/utils'
import {make} from 'vuex-pathify'
import _ from 'lodash'
import mime from 'mime-types'

const initialState = {
  uploadProgress: 0,
  nameOfFileCurrentlyUploading: null,
  autocompleteResults: [],
  fileName: null,
  fileSystem: {files: [], folders: []},
  jobId: null,
  jobPath: null
}

const state = Vue.util.extend({}, initialState)

const getters = make.getters(state)
getters.autocompleteResults = (state) => {
  if (!state.autocompleteResults) {
    return []
  }
  return state.autocompleteResults
}
getters.fileSystem = (state) => {
  if (!state.fileSystem) {
    state.fileSystem = {files: [], folders: []}
  }

  if (!state.fileSystem.files) {
    state.fileSystem.files = []
  }

  if (!state.fileSystem.folders) {
    state.fileSystem.folders = []
  }

  return state.fileSystem
}

const mutations = make.mutations(state)
mutations.addFile = (state, fileName) => {
  state.fileSystem.files.push(fileName)
}
mutations.removeFile = (state, fileName) => {
  state.fileSystem.files = _.filter(state.fileSystem.files, (file) => file !== fileName)
}
mutations.resetFileSystem = (state, _) => {
  state.fileSystem.files = []
  state.fileSystem.folders = []
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async fetchDirectoryContents({commit}, path) {
      let response = await fetchDirectoryContents(path)
      if (OK === response.status) {
        commit('fileSystem', response.data)
      } else if (NOT_FOUND === response.status) {
        const createFolderResponse = await createFolder(path)
        if (CREATED === createFolderResponse.status) {
          response = await fetchDirectoryContents(path)
          commit('fileSystem', response.data)
        }
      }
      return response
    },
    async fetchJobDirectoryContents({}, path) {
      return await fetchDirectoryContents(path)
    },
    async fileNameContains({state}, payload) {
      const url = 'files/search/findByNameContainsIgnoreCaseAndPathOrderByNameAsc?path=' +
        encodeURI(payload.folder) + '&name=' + payload.name

      const response = await ptracGetEntities(url, 'ptracFiles')

      if (OK === response.status) {
        state.autocompleteResults = response.data
      }
    },
    async fetchResults({state}, payload) {
      const url = `files/search/findByNameContainsIgnoreCaseAndPathOrderByNameAsc?name=${payload.query}&path=${payload.path}`

      return await ptracGetEntities(url, 'ptracFiles')
    },
    async fetchAllByPath({}, payload) {
      const url = `files/search/findByPathOrderByNameAsc?path=${payload.path}`

      return await ptracGetEntities(url, 'ptracFiles')
    },
    // for uploading general files to projects, jobs, departments, or tasks
    async upload({state, commit}, payload) {
      try {
        const formData = new FormData()
        for (let i = 0; i < payload.files.length; i++) {
          formData.append('files', payload.files[i])
        }

        const uploadConfig = { headers: await addHeaders(Constants.HEADERS.FORM_DATA.headers) }

        uploadConfig.onUploadProgress = (progressEvent) => {
          state.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        }

        const uploadUrl = `${process.env.VUE_APP_FILE_MANAGER_API_URI}/files?p=${encodeURI(payload.path)}`

        // upload the resource to ATS's File Server
        return await axios.post(uploadUrl, formData, uploadConfig)
      } catch (err) {
        return err.response
      }
    },
    async uploadFilesViaS3({state}, payload) {
      let path = payload.path
      if (path.startsWith('/')) {
        path = path.substring(1) // trim off leading /
      }
      const files = []
      for (let i = 0; i < payload.files.length; i++) {
        files.push(`${path}/${payload.files[i].name}`)
      }

      const response = await presignedS3PutUrlsFileUploads(files)

      const uploadResponses = []

      if (OK === response.status) {
        const uploadUrls = response.data
        for (let i = 0; i < payload.files.length; i++) {
          const file = payload.files[i]
          const uploadUrl = uploadUrls[`${path}/${file.name}`];
          if (!uploadUrl) {
            console.error(`No upload url return for ${path}/${file.name}`)
            continue
          }
          const uploadConfig = {
            headers: {
              'Content-Type':  mime.lookup(file.name)
            },
            onUploadProgress: (progressEvent) => {
              state.uploadProgress = calculateUploadProgressInteger(progressEvent)
            }
          }

          state.nameOfFileCurrentlyUploading = file.name

          await axios.put(uploadUrl, file, uploadConfig)

          uploadResponses.push({
            fileName: file.name,
            status: response.status
          })
        }
      }

      state.nameOfFileCurrentlyUploading = null

      return uploadResponses
    },
    // for uploading Inspection Forms and Floor Plans
    async uploadFiles({state, commit, rootGetters}, payload) {
      const formData = new FormData()

      for (let i = 0; i < payload.files.length; i++) {
        formData.append('files', payload.files[i])
      }

      const uploadConfig = { headers: await addHeaders(Constants.HEADERS.FORM_DATA.headers) }

      uploadConfig.onUploadProgress = (progressEvent) => {
        state.uploadProgress = calculateUploadProgressInteger(progressEvent)
      }

      let uploadUrl = `${process.env.VUE_APP_FILE_MANAGER_API_URI}/files?p=${encodeURI(payload.path)}`

      if (Array.isArray(payload.overwrite) && payload.overwrite.length > 0) {
        uploadUrl += '&overwrite=' + payload.overwrite.map((fileName) => encodeURIComponent(fileName)).join()
      }

      try {
        // upload the resource to ATS's File Server
        const fileUploadResponse = await axios.post(uploadUrl, formData, uploadConfig)

        // add form metadata to ptrac
        if (ACCEPTED === fileUploadResponse.status) {
          let fileMetadata = []
          for (let i = 0; i < payload.files.length; i++) {
            fileMetadata.push({
              name: payload.files[i].name,
              uploadedBy: rootGetters['security/userId'],
              path: payload.path
            })
          }

          return await ptracPostEntity('files/saveAll', fileMetadata)
        }

        return fileUploadResponse
      } catch (error) {
        return error.response
      }
    },
    async uploadUserFiles({}, payload) {
      const formData = new FormData()

      for (let i = 0; i < payload.files.length; i++) {
        formData.append('files', payload.files[i])
      }

      const uploadConfig = { headers: await addHeaders(Constants.HEADERS.FORM_DATA.headers) }

      uploadConfig.onUploadProgress = (progressEvent) => {
        state.totalBytes = progressEvent.total
        state.uploadProgress = progressEvent.loaded
      }

      const uploadUrl = `${process.env.VUE_APP_FILE_MANAGER_API_URI}/files?p=${encodeURI(Constants.FOLDERS.USERS + '/' + payload.path)}`

      // upload the resource to ATS's File Server
      return axios.post(uploadUrl, formData, uploadConfig)
    },
    async uploadCompanyFiles({}, payload) {
      const formData = new FormData()

      for (let i = 0; i < payload.files.length; i++) {
        formData.append('files', payload.files[i])
      }

      const uploadConfig = { headers: await addHeaders(Constants.HEADERS.FORM_DATA.headers) }

      uploadConfig.onUploadProgress = (progressEvent) => {
        state.totalBytes = progressEvent.total
        state.uploadProgress = progressEvent.loaded
      }

      const uploadUrl = `${process.env.VUE_APP_FILE_MANAGER_API_URI}/files?p=${encodeURI(Constants.FOLDERS.COMPANIES + '/' + payload.path)}`

      // upload the resource to ATS's File Server
      return axios.post(uploadUrl, formData, uploadConfig)
    },
    async download({}, payload) {
      const response = await downloadFile(payload)
      if (OK === response.status) {
        saveFileToBrowserDownloads(response.data, payload.name, payload.openFile)
      }
    },
    async downloadSelected({rootGetters}, selectedElasticsearchTasks) {
      const isPdfsLocked = rootGetters['tasks/search/isPdfsLocked']
      const paths = []

      selectedElasticsearchTasks.forEach((selectedTaskResult) => {
        const taskId = selectedTaskResult._id
        const task = selectedTaskResult._source
        const department = rootGetters['departments/byId'](task.job.departmentId)
        const path = fileSystemPathForTask(
          task.project.name,
          task.project.id,
          department.name,
          task.job.name,
          task.job.id,
          task.name,
          taskId,
          getRepeatCount(task)
        )
        paths.push(path)
      })

      const response = await downloadFilesForSelectedPaths(paths, isPdfsLocked)
      if (OK === response.status || 210 === response.status) {
        saveFileToBrowserDownloads(response.data, 'archive.zip')
      }
      return response
    },
    async downloadFlatPdf({}, file) {
      const response = await downloadFlattenedPdf(file)
      if (OK === response.status) {
        saveFileToBrowserDownloads(response.data, file.name)
      }
    },
    async delete({commit}, file) {
      // delete resource from File Server
      const response = await deleteFile(file)

      if (NO_CONTENT === response.status) {
        commit('removeFile', file.name)
      }

      return response
    },
    // for deleting Inspection Forms and Floor Plans
    async deleteOne({commit}, file) {
      // delete resource from File Server
      const response = await deleteFile(file)

      // if the file was successfully deleted from the file server, remove the reference to it in PTRAC
      if (NO_CONTENT === response.status) {
        // delete reference to the resource in ptrac
        return await ptracDeleteEntity(`files/${file.id}`)
      }

      return response
    },
    async validateInUse({}, payload) {
      return await ptracPost('files/validateFilesExist', payload)
    }
  },
  modules: {
    energyPlans,
    floorPlans,
    inspectionForms
  }
}
